import { z } from 'zod';
import { not } from './compute';
import { crewOrderSchema } from './schemas';

type CrewOrder = z.infer<typeof crewOrderSchema>;
type CrewOrderLineItem = CrewOrder['lineItems'][number];

export const hasEligibleReturnResolution = (item: CrewOrderLineItem) =>
  item.return.exchange.isEligible ||
  item.return.refund.isEligible ||
  item.return.giftCard.isEligible;

export const isItemReturnEligible =
  (areReturnsEnabled: boolean) => (item: CrewOrderLineItem) =>
    areReturnsEnabled &&
    !item.return.isFinalSale &&
    item.currentQuantity > 0 &&
    hasEligibleReturnResolution(item);

// a null lastEligibleDate means the item never had a warranty window, which isn't "past" it
const isPastWarrantyWindow = ({ warranty }: CrewOrderLineItem) =>
  !!warranty.lastEligibleDate &&
  new Date(warranty.lastEligibleDate) < new Date();

// a store rule can opt a line item into collecting claims after its window closes.
// the api can still call a past-window item eligible (multiple warranty claims), and its
// verdict wins -- otherwise a selectable item gets the closed-window interstitial
export const isWarrantyClaimAllowedOutsideWindow = (item: CrewOrderLineItem) =>
  !item.warranty.isEligible &&
  !!item.warranty.isIneligibleWarrantyClaimAllowed &&
  isPastWarrantyWindow(item);

export const isItemWarrantyEligible = (item: CrewOrderLineItem) =>
  (item.warranty.isEligible || isWarrantyClaimAllowedOutsideWindow(item)) &&
  item.currentQuantity > 0;

export const checkIfPastReturnWindow = (areReturnsEnabled: boolean) =>
  not(isItemReturnEligible(areReturnsEnabled));
